import { contractRequest } from "./transport";
import type { RecordReview, ReviewTriggerType } from "@/features/patient/records/api/dataPipelineApi";
import type { DecisionAction, PatientResponse, ReviewAnswer } from "@/portals/patient/api/reviewItemsApi";
import type { EmpiDetail } from "@/shared/types/empi";

export type { DecisionAction };

// Module 4 — clinician review queue. Clinician token on every call; failures go
// through the one contract error parser (contractErrors.ts).

export type ClinicianItemStatus = "AWAITING_CLINICIAN" | "IN_CLINICIAN_REVIEW" | "NEEDS_MORE_INFO" | "RESOLVED_PATIENT_REPORTED" | "RESOLVED_CLINICIAN_VERIFIED";
export type AttachedVia = "CLAIMED" | "ASSIGNED_BY_ADMIN";

/** Shared-queue row. Deliberately carries no patient identity. */
export interface QueueItem {
  id: string;
  triggerType: ReviewTriggerType;
  /** null on a GOLDEN_RECORD_CONFLICT about a condition, allergy or lab result. */
  medicineName: string | null;
  recordCount: number;
  createdAt: string;
}

export interface MyReviewItem {
  id: string;
  triggerType: ReviewTriggerType;
  itemStatus: ClinicianItemStatus;
  /** null on a GOLDEN_RECORD_CONFLICT about a condition, allergy or lab result. */
  medicineName: string | null;
  /** Age in years ("90+" from 90) — the clinician side never sees name or date of birth. */
  patientAge?: string | null;
  attachedVia: AttachedVia;
  attachedAt: string;
  /** A question to the patient is still unanswered. */
  awaitingPatientReply?: boolean;
}

export interface RxNormMatch {
  rxcui: string | null;
  matchLevel: string | null;
  confidenceScore: number | null;
  confidenceBand: string | null;
  matchSource: string | null;
}

export interface ClinicianReviewRecord {
  id: string;
  recordRole: RecordReview["recordRole"];
  sourceVendor: string;
  createdAt: string;
  fields: Record<string, unknown>;
  rxnorm: RxNormMatch | null;
}

/** Keys a CORRECT_VALUE decision may change — no others. */
export const CORRECTABLE_FIELDS = ["medication_name", "dose_quantity", "dose_unit", "frequency", "route", "dosage_text", "status"] as const;
export type CorrectableField = (typeof CORRECTABLE_FIELDS)[number];
export type CorrectedValue = Partial<Record<CorrectableField, string | number>>;

/** A recorded correction: the values sent, plus the backend's RxNorm lookup of the
 *  corrected medicine (null when absent or the lookup failed). */
export type RecordedCorrection = CorrectedValue & {
  rxnorm?: Omit<RxNormMatch, "matchSource"> | null;
};

export interface ClinicianAction {
  action: DecisionAction;
  reason: string;
  targetRecordId: string | null;
  correctedValue: RecordedCorrection | null;
  createdAt: string;
}

export interface DecisionRequest {
  action: DecisionAction;
  reason: string;
  targetRecordId?: string;
  correctedValue?: CorrectedValue;
}

export const REASON_MAX_LENGTH = 2000;

/** What's inside a task — the same for the queue preview, the held item and
 *  the admin's view: the issue, its records and the whole conversation. The
 *  patient appears only as an age (never name or date of birth). */
export interface QueueItemPreview {
  id: string;
  triggerType: ReviewTriggerType;
  itemStatus: ClinicianItemStatus;
  createdAt: string;
  records: ClinicianReviewRecord[];
  /** Age in years ("90+" from 90); null when no date of birth is on file. */
  patientAge?: string | null;
  /** Oldest first. */
  patientResponses?: PatientResponse[];
  /** Oldest first. */
  clinicianActions?: ClinicianAction[];
  /** A question to the patient is still unanswered (the clinician can decide anyway). */
  awaitingPatientReply?: boolean;
  /** What the patient may answer on this kind of item (labels their replies). */
  allowedAnswers: ReviewAnswer[];
  /** GOLDEN_RECORD_CONFLICT only: the conflicting values with their sources.
   *  Never an identity block — those items are patient-only. */
  empi?: EmpiDetail;
}

export interface ClinicianReviewItem extends QueueItemPreview {
  attachedVia: AttachedVia;
  attachedAt: string;
  /** Oldest first. */
  patientResponses: PatientResponse[];
}

/** Oldest first. Only AWAITING_CLINICIAN items nobody holds. */
export const listReviewQueueApi = () =>
  contractRequest<{ items: QueueItem[] }>("/api/clinician/review-queue", { method: "GET", portal: "clinician" });

/** Read-only, changes nothing. 200 preview · 409 ITEM_ALREADY_CLAIMED (left the queue) · 404 REVIEW_ITEM_NOT_FOUND */
export const previewQueueItemApi = (id: string) =>
  contractRequest<QueueItemPreview>(`/api/clinician/review-queue/${encodeURIComponent(id)}`, { method: "GET", portal: "clinician" });

/** 200 item · 409 ITEM_ALREADY_CLAIMED · 404 REVIEW_ITEM_NOT_FOUND */
export const claimReviewItemApi = (id: string) =>
  contractRequest<ClinicianReviewItem>(`/api/clinician/review-queue/${encodeURIComponent(id)}/claim`, { method: "POST", portal: "clinician" });

/** "active" (default) = IN_CLINICIAN_REVIEW or NEEDS_MORE_INFO held by me. */
export const listMyReviewItemsApi = (state: "active" | "all" = "active") =>
  contractRequest<{ items: MyReviewItem[] }>(`/api/clinician/review-items?state=${state}`, { method: "GET", portal: "clinician" });

/** 404 REVIEW_ITEM_NOT_FOUND when not held by me. */
export const getClinicianReviewItemApi = (id: string) =>
  contractRequest<ClinicianReviewItem>(`/api/clinician/review-items/${encodeURIComponent(id)}`, { method: "GET", portal: "clinician" });

/** 200 · 409 ITEM_NOT_RELEASABLE · 404 REVIEW_ITEM_NOT_FOUND */
export const releaseReviewItemApi = (id: string) =>
  contractRequest<{ id: string; itemStatus: "AWAITING_CLINICIAN" }>(`/api/clinician/review-items/${encodeURIComponent(id)}/release`, { method: "POST", portal: "clinician" });

/** 200 updated item · 404 REVIEW_ITEM_NOT_FOUND · 409 ITEM_NOT_DECIDABLE · 400 VALIDATION_ERROR */
export const decideReviewItemApi = (id: string, body: DecisionRequest) =>
  contractRequest<ClinicianReviewItem>(`/api/clinician/review-items/${encodeURIComponent(id)}/decisions`, {
    method: "POST",
    portal: "clinician",
    body,
  });
